import { useEffect, useState } from 'react'
import { api } from './api'

/**
 * Mirror of Spring Data's `Page<T>` JSON (the fields the UI consumes).
 * The backend read endpoints (`/api/shifts`, `/api/users`, `/api/stores`)
 * now return this envelope instead of a bare array.
 */
export interface Page<T> {
  content: T[]
  totalElements: number
  totalPages: number
  number: number          // 0-based index of the current page
  size: number
  first: boolean
  last: boolean
  numberOfElements: number
  empty: boolean
}

export const DEFAULT_PAGE_SIZE = 20

/**
 * Normalizes a list endpoint's response body to an array, tolerating every
 * shape the backend — or an error — can produce:
 *   - a bare array               → returned as-is
 *   - a Spring `Page` envelope    → its `content` array
 *   - anything else (an error/401 body, null, a string, an object) → `[]`
 *
 * List pages call `.filter()` / `.map()` / `.length` on this directly, so
 * guaranteeing an array here is what stops a non-array response (e.g. a 401
 * error body that reaches `.then()`) from white-screening the whole view.
 * Prefer this over `r.data.content || []`, which returns `[]` for endpoints
 * that legitimately return a bare array (silently hiding their data).
 */
export function toArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[]
  if (data && typeof data === 'object' && Array.isArray((data as { content?: unknown }).content)) {
    return (data as { content: T[] }).content
  }
  return []
}

/**
 * Fetches a paginated (Spring `Page`) endpoint and tracks page state.
 * Sends `?page=&size=`; re-fetches when the page changes.
 */
export function usePage<T>(path: string, size: number = DEFAULT_PAGE_SIZE) {
  const [page, setPage] = useState(0)
  const [data, setData] = useState<Page<T> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    api.get<Page<T>>(path, { params: { page, size } })
      .then(r => { if (!cancelled) setData(r.data) })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [path, page, size])

  return { data, loading, error, page, setPage }
}
